/**
 * Fetch helpers for the operational dashboard widgets (FacilityStatus,
 * ArrivalsDepartures, StaffOnDuty, ComplianceAlerts). Co-located with the
 * widget files because each widget's response shape lives alongside its
 * consumer.
 *
 * As of 2026-05-11 (Track C cross-review follow-up), `fetchWithStaffAuth`
 * delegates to `api.get` from `../../lib/api.js` so every dashboard fetch
 * inherits Track C's cold-start retry policy and triggers the
 * `ServerWakingBanner` on Railway wake. Public signatures of the named
 * fetchers below are preserved — consumers don't change.
 *
 * The `token` parameter is retained for back-compat: `api.get` reads the
 * staff token from `readSession()` itself, so the value passed here is
 * effectively unused. The prop is kept so call sites (FacilityStatus.tsx
 * etc.) compile without churn.
 */
import { api, ApiError, type AteNoneWarning } from '../../lib/api.js';
// Compliance roster types are defined once in the canonical api client
// (lib/api.ts, issue #109) and re-exported here so the dashboard
// ComplianceAlerts widget imports them co-located with its fetcher
// (`fetchComplianceSummary`) without a second source of truth.
import {
  type ComplianceCounts,
  type ComplianceResponse,
  type ComplianceRow,
  type ComplianceStatus,
  type DogCard,
  type DogFlag,
  type TransportMode,
} from '../../lib/api.js';
export type {
  ComplianceCounts,
  ComplianceResponse,
  ComplianceRow,
  ComplianceStatus,
  DogCard,
};

export type DashboardServiceKey = 'daycare' | 'boarding' | 'grooming';

/**
 * Where a capacity denominator came from (Lane A2, 2026-07-09 wave):
 * 'rules' = resolved through the booking engine's CapacityRule/
 * CapacityOverride reads — a REAL denominator; 'default' = the tenant has
 * no applicable rule and the server refused to invent one (`capacity`
 * is null in the per-service entry). Never render a 'default' number as
 * configured truth (DESIGN-PHILOSOPHY Principle 5).
 */
export type CapacitySource = 'rules' | 'default';

export interface ServiceOccupancy {
  onSite: number;
  /**
   * Rule-resolved capacity; null = unconfigured (unknown — show "not
   * set", never a made-up denominator). 0 = closed-day override:
   * genuinely full/closed, not missing data.
   */
  capacity: number | null;
  capacitySource: CapacitySource;
}

export interface FacilityStatusResponse {
  onSite: number;
  capacity: number;
  /** 'default' ⇒ `capacity` is the legacy fallback constant, not rules. */
  capacitySource?: CapacitySource;
  serviceBreakdown: {
    daycare: number;
    boarding: number;
    grooming: number;
  };
  /** Per-service occupancy with real denominators (Lane A2). */
  services?: Record<DashboardServiceKey, ServiceOccupancy>;
}

export interface DashboardArrival {
  bookingId: string;
  /** Ted #7 (#910) — the owner behind the row. Optional: older servers omit it. */
  customerId?: string | null;
  /**
   * Ted 10-09 (#915) — false only on a STAFF booking whose client was never
   * sent a confirmation; null when not applicable or unknown; absent from an
   * older server. Render on `=== false` only.
   */
  customerNotified?: boolean | null;
  customerName: string;
  dogNames: string[];
  // #545/#546 — dog-first card data (photo/breed/sex/altered + vax shield).
  // Optional: older servers omit it and the widget falls back to dogNames.
  dogs?: DogCard[];
  /** #546 — the booking has an OPEN open-invoice (balance still due). */
  unpaid?: boolean;
  balanceDueCents?: number;
  serviceType: string;
  /**
   * Ted #17 (#921): the customer-facing service name and the owner's palette
   * key for it. Optional: older servers omit both (the row shows the engine
   * key, neutral).
   */
  serviceDisplayName?: string;
  serviceColor?: string | null;
  expectedTime: string | null;
  status: string;
  /**
   * #564 — any dog on the booking has med instructions on file. Optional:
   * servers older than #564 omit it, and the morning board treats absence
   * as "no meds flag" (never a false MEDS badge).
   */
  hasMedications?: boolean;
  /**
   * Ted #7 (#910) — the owner's store credit (wallet balance) in cents, so
   * the desk sees it on Going Home Today BEFORE anything is added to the
   * cart. 0 for money-blind roles and owners with no credit; the row shows
   * nothing at 0. Optional: older servers omit it (never a false credit).
   */
  walletBalanceCents?: number;
  /**
   * Wave 5 (#919): the customer-requested drop-off / pick-up, HH:MM, so the
   * day view can show WHEN a dog is in the building, not only that it is.
   * Optional: servers older than wave 5 omit both (the row shows no time).
   */
  dropOffTime?: string | null;
  pickUpTime?: string | null;
  // ── Ted #23 (#984) review fix — SERVICE-LEG FACTS ────────────────────────
  // A leg is a Booking, so it was ALWAYS on this feed; without these fields
  // it rendered as an untagged second arrival row for a dog arriving once,
  // labelled with the service type it rides on ("Grooming"). Optional for
  // deploy skew: frontends auto-deploy on merge and the server does not, so
  // absent must read as "an ordinary booking" — the pre-#984 list.
  /** The visit this row hangs off, when it is an added service. */
  parentBookingId?: string | null;
  /** True when this row IS an added service rather than a visit of its own. */
  isLeg?: boolean;
  /** The add-on's catalog name ("Nail Trim") — what the row should READ as. */
  addOnName?: string | null;
  /** Ted's rule: a leg with nobody assigned lands in a lane, never nowhere. */
  needsSpecialist?: boolean;
  // ── Ted #28 (#1050) — transport on the stay ──────────────────────────────
  // The per-dog `dogs[].flags` are the render form; these are the facts the
  // check-in surfaces branch on. Optional for deploy skew: absent ⇒ 'none'.
  transport?: TransportMode;
  arrivalVerifiedAt?: string | null;
  // ── Ted 09-29 (#1111, lane LC) ───────────────────────────────────────────
  /** Isolated mid-stay (in heat). Optional for deploy skew: absent ⇒ not isolated. */
  isolatedAt?: string | null;
  // Sawyer 10 customer-booking-verify-policy (C5 C6) — the owner's attestations at reservation.
  /** "I have verified …" ticked when booking. Optional for deploy skew: absent ⇒ nothing renders. */
  careAttestedAt?: string | null;
  /** Agreed to the medication policy when booking. Optional for deploy skew. */
  medicationPolicyAckAt?: string | null;
  /**
   * The owner's "not in heat" declaration from the reservation (lane LG
   * writes it; shown at drop-off ONLY when the field is present).
   */
  heatDeclaration?: 'not_in_heat' | null;
  heatDeclaredAt?: string | null;
  // ── Sawyer C2/G8/A4 (lane 03) ────────────────────────────────────────────
  // Optional for deploy skew: the frontend auto-deploys before the server,
  // so an older server omits all three and every surface renders today's row.
  /** The visit's note (Gingr label stripped server-side); null = nothing to read. */
  bookingNote?: string | null;
  /**
   * Sawyer #1296 — true once that note was kept in the owner's notes; null =
   * the server could not tell; absent = a server older than #1296. The
   * owner's-notes prompt at check-in asks only on `=== false`.
   */
  bookingNoteInOwnerNotes?: boolean | null;
  /**
   * The desk's per-stay note per dog (`booking_dogs.run_card_edits.note`).
   * Read it through `deskNotesOf`: present (even `[]`) means this server
   * carries desk notes and the Front Desk may offer "Add note"; absent means
   * an older server (no control).
   */
  deskNotes?: DeskNote[];
  /** This row's add-on is the tenant's meal charge (legs only; read by lunch-marker.ts). */
  addOnIsMealCharge?: boolean;
  // ── Sawyer answers 10-29, item 2 — "Going home today" ───────────────────
  // Optional for deploy skew: the frontend auto-deploys before the server, so
  // an older server omits both and the list falls back to the checked-in
  // departures with no belongings / locker / food columns (never "none").
  /** This visit checks out today (a departure, or an arrival whose stay ends today). Never true on a leg. */
  checksOutToday?: boolean;
  /**
   * On a row with `checksOutToday`: the per-dog going-home facts. `null` = the
   * server could not load them ("couldn't load", never "nothing to send").
   */
  goingHome?: GoingHomeCare | null;
}

/** Sawyer 10-29 item 2 — the server's food rule (apps/server/src/lib/going-home.ts). */
export type GoingHomeFoodStatus = 'send_home' | 'house_food' | 'no_meals' | 'unknown';
export type GoingHomeFoodReason =
  | 'in_belongings'
  | 'own_food'
  | 'part_house_food'
  | 'house_food'
  | 'no_meals_here'
  | 'house_food_not_recorded'
  | 'service_unknown';

export interface GoingHomeFood {
  status: GoingHomeFoodStatus;
  reason: GoingHomeFoodReason;
  /** Belongings items that are food; they show in the food flag, not in belongings. */
  foodItems: string[];
}

export interface GoingHomeDogCare {
  dogId: string;
  belongings: string[];
  locker: string | null;
  lockerSource: 'stay' | 'bed_default' | 'none';
  food: GoingHomeFood;
}

export interface GoingHomeCare {
  /** HH:MM, the requested pick-up (else an appointment's end); null when not set. */
  pickUpTime: string | null;
  dogs: GoingHomeDogCare[];
}

export type DashboardDeparture = DashboardArrival;

export interface DashboardTodayResponse {
  arrivals: DashboardArrival[];
  departures: DashboardDeparture[];
  /**
   * Wave 5 (#919 #922; Ted's Lodging rule): checked-in multi-day stays that
   * neither arrive nor leave today — the mid-stay boarders `/today` used to
   * drop. Optional: servers older than wave 5 omit it, and every existing
   * consumer keeps reading arrivals/departures only.
   */
  onSite?: DashboardArrival[];
  /**
   * Ted #22 (#983) — every booking checked out TODAY that still has an open,
   * unsettled invoice. This is the answer to "where did Juniper go": the row
   * leaves Going Home the instant it is checked out, and nothing pointed at
   * what it left behind. Keyed on the PRESENCE of the invoice, not on a
   * positive balance — Juniper's was $0.00.
   *
   * Optional: servers older than #983 omit it (frontends auto-deploy on merge
   * and the server does not), and A/R-blind roles get []. Absent ⇒ the strip
   * does not render, which is exactly today's behaviour.
   */
  outstandingToday?: OutstandingCheckout[];
}

/** Ted #22 (#983) — one unsettled checkout on the Front Desk strip. */
export interface OutstandingCheckout {
  bookingId: string;
  customerId: string | null;
  customerName: string;
  dogNames: string[];
  serviceType: string;
  serviceDisplayName: string | null;
  openInvoiceId: string;
  amountDueCents: number;
  checkedOutAt: string | null;
}

// ── Wave 5 (#919 #922 #940 Part A) — the day rollup ───────────────────
//
// `GET /api/admin/dashboard/rollup?date=` — counts AND the rows they were
// counted from, in one read, so a tapped number opens exactly the dogs it
// counted. Shapes mirror the server payload (apps/server/src/routes/
// admin-dashboard.ts `/rollup`, folded by packages/core/modules/booking/
// day-semantics.ts). Every number is DISTINCT DOGS.

/** The six words on a day-list row (Ted #15's status list). */
export type DayListStatus =
  | 'requested'
  | 'unconfirmed'
  | 'expected'
  | 'checked_in'
  | 'going_home'
  | 'checked_out';

/** Ted #15's five columns. */
export type RollupCell = 'expected' | 'checkIns' | 'checkOuts' | 'overnight' | 'total';

export interface DayRollupDog {
  id: string;
  name: string;
  breed: string | null;
  photoUrl: string | null;
  /** Ted #28 (#1050) — the stay's flags; absent on an older server. */
  flags?: DogFlag[];
  /**
   * Sawyer 08 meds-feeding (A3) — the row's feeding / medication times.
   * Absent on an older server and null on grooming rows, service legs and
   * for callers with no pets read: the line then renders nothing.
   */
  care?: DayRollupCare | null;
  /**
   * Sawyer #1295 — the meals this dog ate none of on the list's day. Absent
   * on an older server or for a caller with no pets read; null when none.
   */
  ateNone?: AteNoneWarning | null;
}

/** Sawyer 08 meds-feeding (A3) — mirrors core `DayListCare`. */
export interface DayRollupCare {
  meals: Array<'breakfast' | 'lunch' | 'dinner'>;
  mealSources: Partial<Record<'breakfast' | 'lunch' | 'dinner', 'set' | 'house_default' | 'meal_add_on'>>;
  eatsLunch: boolean;
  meds: Array<{
    name: string;
    slots: string[];
    slotSource: 'set' | 'frequency' | 'frequency_default' | 'unscheduled';
    /** core placeMedicationFor; optional because a server from before review round 1 omits it. */
    placement?: 'scheduled' | 'at_home' | 'times_not_set';
  }>;
  hasMedicationNotes: boolean;
  hasFeedingNotes: boolean;
}

export interface DayRollupItem {
  bookingId: string;
  customerId: string | null;
  customerName: string;
  dogs: DayRollupDog[];
  serviceTypeId: string;
  /** The engine key (`daycare`, `boarding`, …). */
  serviceType: string;
  serviceDisplayName: string;
  /** Ted #17 (#921): the service's palette key, or null (neutral). */
  color: string | null;
  /** Raw booking status. */
  status: string;
  listStatus: DayListStatus;
  startsToday: boolean;
  endsToday: boolean;
  /** Holds a bed tonight — never true on a stay's last day (Ted's Lodging rule). */
  staysTonight: boolean;
  /** Ted A6: counts toward the total-on-site limit (daycare/boarding/assessment). */
  onFloor: boolean;
  /** YYYY-MM-DD. */
  date: string;
  startDate: string | null;
  endDate: string | null;
  /** HH:MM (grooming appointments). */
  startTime: string | null;
  /** HH:MM, customer-requested. */
  dropOffTime: string | null;
  pickUpTime: string | null;
  bookingSource: string | null;
  linkedBookingId: string | null;
  // ── Ted #23 (#984) / Ted #20 Part B (#940) — SERVICE LEGS ────────────────
  // Optional for deploy skew: an older server omits them and every row reads
  // as an ordinary booking, which is exactly the pre-#984 list.
  /** The visit this row hangs off, when it is an added service. */
  parentBookingId?: string | null;
  /** True when this row is an added service rather than the visit itself. */
  isLeg?: boolean;
  /** The add-on's catalog name ("Nail Trim") — what the row should READ as. */
  addOnName?: string | null;
  /** Groomer or generic assignee, whichever the leg carries. */
  assigneeId?: string | null;
  /** Ted's rule: a leg with nobody assigned must land in a lane, never nowhere. */
  needsSpecialist?: boolean;
}

export interface DayRollupCounts {
  expected: number;
  checkIns: number;
  checkOuts: number;
  overnight: number;
  total: number;
}

export interface DayRollupServiceRow extends DayRollupCounts {
  serviceTypeId: string;
  name: string;
  displayName: string;
  /** Ted #17 (#921): the owner's palette key for the row, or null (neutral). */
  color: string | null;
}

/** Ted #18: ON SITE NOW · ARRIVING · GOING HOME · OVERNIGHT TONIGHT (+ the on-floor pair for A5/A6). */
export interface DaySnapshot {
  onSiteNow: number;
  arriving: number;
  goingHome: number;
  overnightTonight: number;
  onFloorNow: number;
  onFloorPlannedToday: number;
}

export interface FacilityMeter {
  onFloorNow: number;
  onFloorPlannedToday: number;
  limit: number;
  limitSource: 'configured' | 'default';
  tone: 'green' | 'amber' | 'red';
}

export interface StaffingNeed {
  /** null = the owner has not set a ratio; nothing invents one. */
  dogsPerHandler: number | null;
  onFloorNow: number;
  onFloorPlannedToday: number;
  handlersNeededNow: number | null;
  handlersNeededToday: number | null;
  /**
   * Ted A5 (#933 PR 4) — whether the owner's ratio is being met right now.
   * Optional: an older server sends the need without the coverage, and the
   * hero renders no warning rather than a false one.
   */
  handlersOnNow?: number;
  handlersShort?: number | null;
  /** amber = exactly at the ratio; red = below the owner's own rule. */
  tone?: 'green' | 'amber' | 'red';
}

export interface DashboardRollupResponse {
  date: string;
  services: DayRollupServiceRow[];
  totals: DayRollupCounts;
  snapshot: DaySnapshot;
  facility: FacilityMeter;
  staffing: StaffingNeed;
  items: DayRollupItem[];
}

export interface FacilityDetailDog {
  dogId: string;
  dogName: string;
  breed: string | null;
  sizeCategory: string | null;
  photoUrl: string | null;
  ownerId: string;
  ownerName: string;
  checkInTime: string | null;
  status: string;
  notes: string | null;
  /** Ted #28 (#1050) — the stay's flags; absent on an older server. */
  flags?: DogFlag[];
}

export interface FacilityDetailsResponse {
  dogs: FacilityDetailDog[];
  count: number;
}

async function fetchWithStaffAuth<T>(
  apiUrl: string,
  _token: string,
  path: string,
): Promise<T> {
  // `_token` is intentionally unused — `api.get` pulls the staff token
  // from `readSession()` itself. The param is preserved so the named
  // fetchers below don't need to change their public signatures.
  const result = await api.get<T>(apiUrl, path);
  if (result.error !== undefined) {
    // The wrapper sanitizes the HTTP status into `{ error, isRetryable? }`;
    // the raw status is no longer available. Use 503 as a sentinel when
    // the error was a cold-start (so any consumer doing
    // `err.status === 503` still works) and 0 otherwise.
    throw new ApiError(result.isRetryable ? 503 : 0, result.error);
  }
  if (result.data === undefined) {
    throw new ApiError(0, 'Empty server response');
  }
  return result.data;
}

export function fetchFacilityStatus(
  apiUrl: string,
  token: string,
  date: string,
): Promise<FacilityStatusResponse> {
  return fetchWithStaffAuth<FacilityStatusResponse>(
    apiUrl,
    token,
    `/api/admin/dashboard/facility-status?date=${encodeURIComponent(date)}`,
  );
}

export function fetchDashboardToday(
  apiUrl: string,
  token: string,
  date: string,
): Promise<DashboardTodayResponse> {
  return fetchWithStaffAuth<DashboardTodayResponse>(
    apiUrl,
    token,
    `/api/admin/dashboard/today?date=${encodeURIComponent(date)}`,
  );
}

/** Wave 5 (#919 #922 #940 Part A) — the day rollup + its rows, one read. */
export function fetchDashboardRollup(
  apiUrl: string,
  token: string,
  date: string,
): Promise<DashboardRollupResponse> {
  return fetchWithStaffAuth<DashboardRollupResponse>(
    apiUrl,
    token,
    `/api/admin/dashboard/rollup?date=${encodeURIComponent(date)}`,
  );
}

export function fetchFacilityDetails(
  apiUrl: string,
  token: string,
  date: string,
  service: 'daycare' | 'boarding' | 'grooming',
): Promise<FacilityDetailsResponse> {
  return fetchWithStaffAuth<FacilityDetailsResponse>(
    apiUrl,
    token,
    `/api/admin/dashboard/facility-details?date=${encodeURIComponent(
      date,
    )}&service=${service}`,
  );
}

/**
 * Shared capacity-tone thresholds (spec B3): ≥90% full = red,
 * ≥70% = amber, else green. Single source of truth for any occupancy
 * meter (FacilityStatus today; the dashboard hero's per-service meters
 * next). Consumers map the tone onto ferro-* modifier classes — the
 * dead Tailwind class helpers that used to live here
 * (capacityColorClass/capacityTextClass/capacityBgClass) had zero
 * importers and are removed (admin-app has no Tailwind build; see
 * no-inert-tailwind.test.ts).
 */
export function capacityTone(pct: number): 'green' | 'amber' | 'red' {
  if (pct >= 90) return 'red';
  if (pct >= 70) return 'amber';
  return 'green';
}

export function formatTime(timeStr: string | null): string {
  if (!timeStr) return '';
  // Accept either an HH:MM grooming slot or an ISO datestamp.
  if (/^\d{1,2}:\d{2}$/.test(timeStr)) return timeStr;
  try {
    const d = new Date(timeStr);
    if (Number.isNaN(d.getTime())) return timeStr;
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return timeStr;
  }
}

// ---------- Wave 2 (parallel-A2-2026-05-11) ----------

export interface StaffShift {
  shiftId: string;
  staffId: string;
  name: string;
  role: string;
  shiftStart: string;
  shiftEnd: string;
}

/** #427 W1 — one open (non-voided) punch: someone on site right now. */
export interface StaffOnSiteEntry {
  staffId: string;
  name: string;
  clockedInAt: string;
  onBreak: boolean;
}

/**
 * #427 W1 — the additive `onSite` block on staff-on-duty: REAL punches
 * (open TimeEntry rows), as opposed to `shifts`/`totalOnShift`, which
 * are the schedule (intent). The staffing tile shows both truths and
 * never conflates them (DESIGN-PHILOSOPHY Principle 5).
 */
export interface StaffOnSiteBlock {
  count: number;
  onBreak: number;
  staff: StaffOnSiteEntry[];
}

export interface StaffOnDutyResponse {
  date: string;
  ratio: string;
  totalOnShift: number;
  onSiteDogs: number;
  roleBreakdown: Record<string, number>;
  shifts: StaffShift[];
  /**
   * Optional: servers older than #427 W1 (and pinned test fixtures)
   * don't send it — consumers must treat absence as "no punch data",
   * NOT as zero staff on site.
   */
  onSite?: StaffOnSiteBlock;
}

// ─── #719 — "Assessments today" ────────────────────────────────────────

/**
 * Assessment-desk readiness for one dog. Distinct from `DogCard.vax` (the
 * arrivals-board shield, which grades EXPIRY only): this one reads
 * `vaccinations.verified` too, because the thing Kylie does before every
 * assessment is verify, and "uploaded but not entered" is the failure she
 * described.
 */
export type VaxVerificationLevel =
  | 'expired'
  | 'missing'
  | 'unverified'
  /** Ted #32a (E1.7) — in date, unsigned, from Gingr or the desk: not a task. */
  | 'on_file'
  | 'verified';

export interface VaxVerificationStatus {
  level: VaxVerificationLevel;
  /** Server-composed sentence — chips are never color-only. */
  detail: string;
  /**
   * A #551 verification-queue item is waiting whose matcher SUGGESTED this
   * dog. `null` = the queue could not be read — render as unknown, NEVER as
   * "nothing pending" (that would tell the desk a dog is clear while its
   * records sit unreviewed).
   */
  queuePending: boolean | null;
}

export interface AssessmentDog extends DogCard {
  vaxVerification: VaxVerificationStatus;
}

export interface AssessmentBooking {
  bookingId: string;
  status: string;
  expectedTime: string | null;
  /** #537 — null for callers without `customers:read:all`. */
  customerId: string | null;
  customerName: string | null;
  /** false ⇒ owner identity was withheld by role, not absent. */
  customerVisible: boolean;
  dogs: AssessmentDog[];
  /** At least one dog is not clear to walk in. */
  needsAttention: boolean;
}

export interface AssessmentsTodayResponse {
  date: string;
  /**
   * Does THIS tenant offer assessments (#718's tenant switch)? False for
   * every tenant in production today. `false` is a DESIGNED state with its
   * own copy — never render it as "no assessments booked".
   */
  configured: boolean;
  /** 'unavailable' ⇒ the verification queue could not be read (unknown). */
  queueStatus: 'available' | 'unavailable';
  count: number;
  assessments: AssessmentBooking[];
}

export function fetchAssessmentsToday(
  apiUrl: string,
  token: string,
  date: string,
): Promise<AssessmentsTodayResponse> {
  return fetchWithStaffAuth<AssessmentsTodayResponse>(
    apiUrl,
    token,
    `/api/admin/dashboard/assessments-today?date=${encodeURIComponent(date)}`,
  );
}

export type ComplianceIssueType =
  | 'vaccination_expired'
  | 'vaccination_unverified';

export interface ComplianceAlert {
  dogId: string;
  dogName: string;
  customerId: string;
  ownerName: string;
  issue: string;
  issueType: ComplianceIssueType;
  bookingDate: string;
}

export interface ComplianceAlertsResponse {
  date: string;
  alerts: ComplianceAlert[];
  count: number;
}

export function fetchStaffOnDuty(
  apiUrl: string,
  token: string,
  date: string,
): Promise<StaffOnDutyResponse> {
  return fetchWithStaffAuth<StaffOnDutyResponse>(
    apiUrl,
    token,
    `/api/admin/dashboard/staff-on-duty?date=${encodeURIComponent(date)}`,
  );
}

export function fetchComplianceAlerts(
  apiUrl: string,
  token: string,
  date: string,
): Promise<ComplianceAlertsResponse> {
  return fetchWithStaffAuth<ComplianceAlertsResponse>(
    apiUrl,
    token,
    `/api/admin/dashboard/compliance-alerts?date=${encodeURIComponent(date)}`,
  );
}

// ─── Lane D2 (2026-07-09 wave) — data honesty (Principle 5) ─────────────
//
// GET /api/admin/dashboard/data-status (Lane A1) — the same presence
// facts AIM's get_today_summary composes (PR #384, the 2026-07-09 HTHD
// incident). `warning` is deterministic SERVER-side copy and must be
// surfaced verbatim; the client never re-words it.

export interface DashboardDataStatus {
  date: string;
  /** The tenant has at least one native booking row (any date/status). */
  bookingsLoaded: boolean;
  /** The tenant has at least one staff-schedule row (any date). */
  staffSchedulesLoaded: boolean;
  /** Historical visit rows in the read-only Gingr mirror. */
  gingrVisitCount: number;
  /** Days since the newest gingr_import booking row; null = none exist. */
  gingrMirrorAgeDays: number | null;
  /** Server-composed warning when zeros are structural; null = trusted. */
  warning: string | null;
}

export function fetchDashboardDataStatus(
  apiUrl: string,
  token: string,
  date: string,
): Promise<DashboardDataStatus> {
  return fetchWithStaffAuth<DashboardDataStatus>(
    apiUrl,
    token,
    `/api/admin/dashboard/data-status?date=${encodeURIComponent(date)}`,
  );
}

/**
 * Stale-mirror window — mirrors the server's composeTodayDataStatus
 * bounds (packages/core/modules/ai-manager/data-status.ts). Below MIN
 * the sync is doing its job; above MAX the tenant has cut over to
 * native bookings and zeros mean zeros again.
 */
const STALE_MIRROR_MIN_DAYS = 2;
const STALE_MIRROR_MAX_DAYS = 90;

/**
 * Should today's SCHEDULE zeros be distrusted? True when the server
 * issued a warning AND the cause is booking-shaped (nothing loaded, or
 * the Gingr mirror is inside the stale window). Widgets showing today's
 * arrivals/departures switch their empty states to honest copy
 * ("Today's schedule isn't in this system yet") instead of a cheerful
 * "No arrivals scheduled" — the 2026-07-09 incident class.
 *
 * A warning caused ONLY by never-entered staff schedules does NOT make
 * the booking data suspect — the staffing tile branches on
 * `staffSchedulesLoaded` separately.
 */
export function isScheduleDataSuspect(
  status: Pick<
    DashboardDataStatus,
    'warning' | 'bookingsLoaded' | 'gingrMirrorAgeDays'
  >,
): boolean {
  if (status.warning === null) return false;
  if (!status.bookingsLoaded) return true;
  return (
    status.gingrMirrorAgeDays !== null &&
    status.gingrMirrorAgeDays >= STALE_MIRROR_MIN_DAYS &&
    status.gingrMirrorAgeDays <= STALE_MIRROR_MAX_DAYS
  );
}

// The #109 fetchComplianceSummary helper (tenant-wide roster preview for
// the old dashboard ComplianceAlerts widget) was removed in the
// 2026-07-09 wave (spec D3): the roster lives on /compliance (which uses
// lib/api.ts's fetchComplianceRoster directly) and the dashboard's
// today-scoped alert strip uses fetchComplianceAlerts above.

// Sawyer 03 today-notes-board (G8) — the desk's same-day note for one dog.
export interface DeskNote {
  dogId: string;
  dogName: string;
  note: string;
}

/**
 * Parse a /today row's `deskNotes` at the boundary (the fetch itself is an
 * unparsed cast). null ⇒ the key is absent (an older server: no desk notes,
 * no "Add note"); otherwise the well-formed entries with non-blank text.
 */
export function deskNotesOf(item: Pick<DashboardArrival, 'deskNotes'>): DeskNote[] | null {
  const raw: unknown = item.deskNotes;
  if (!Array.isArray(raw)) return null;
  const out: DeskNote[] = [];
  for (const entry of raw as unknown[]) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { dogId, dogName, note } = entry as Record<string, unknown>;
    if (typeof dogId !== 'string' || typeof dogName !== 'string' || typeof note !== 'string') continue;
    if (note.trim().length === 0) continue;
    out.push({ dogId, dogName, note });
  }
  return out;
}
